import { useEffect, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, AlertCircle, CheckCircle } from 'lucide-react';

interface InviteData {
  email: string;
  role: string;
  tenantId: string;
}

export default function InviteAcceptPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('invite');

  const [step, setStep] = useState<'loading' | 'form' | 'success' | 'error'>('loading');
  const [inviteData, setInviteData] = useState<InviteData | null>(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  // Validate and decode invitation token
  useEffect(() => {
    const validateInvite = async () => {
      if (!token) {
        setErrorMessage('No invitation token provided');
        setStep('error');
        return;
      }

      try {
        const res = await fetch(`/api/auth/invite/${token}`, {
          credentials: 'include',
        });

        if (!res.ok) {
          setErrorMessage('Invitation expired or invalid');
          setStep('error');
          return;
        }

        const data = await res.json();
        if (data.success) {
          setInviteData(data.data);
          setStep('form');
        } else {
          setErrorMessage(data.error?.message || 'Invalid invitation');
          setStep('error');
        }
      } catch (err) {
        setErrorMessage('Failed to validate invitation');
        setStep('error');
      }
    };

    validateInvite();
  }, [token]);

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    if (!password) {
      newErrors.password = 'Password is required';
    } else if (password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Password strength check: at least 1 uppercase, 1 number, 1 special char
    const strongRegex = /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*])/;
    if (password && !strongRegex.test(password)) {
      newErrors.password = 'Password must contain uppercase letter, number, and special character';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const res = await fetch('/api/auth/invite-accept', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      const data = await res.json();

      if (data.success) {
        setStep('success');
        // Save tenantId to localStorage for next login
        if (inviteData?.tenantId) {
          localStorage.setItem('_pendingTenantId', inviteData.tenantId);
        }
        // Redirect to login after 2 seconds
        setTimeout(() => {
          if (inviteData?.tenantId) {
            navigate(`/login?tenantId=${inviteData.tenantId}`);
          } else {
            navigate('/login');
          }
        }, 2000);
      } else {
        setErrorMessage(data.error?.message || 'Failed to activate account');
        setStep('error');
      }
    } catch (err) {
      setErrorMessage('Network error. Please try again.');
      setStep('error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-purple-500 to-purple-700 p-5 font-sans">
      <div className="w-full max-w-md bg-white rounded-xl shadow-2xl p-10">
        {/* Loading State */}
        {step === 'loading' && (
          <div className="text-center text-gray-600">
            <div className="text-3xl mb-4">⏳</div>
            <p>Validating your invitation...</p>
          </div>
        )}

        {/* Form State */}
        {step === 'form' && inviteData && (
          <>
            <h1 className="text-3xl mb-2 text-slate-900">
              Completa la Registrazione
            </h1>
            <p className="text-gray-600 text-sm mb-6">
              Imposta una password sicura per attivare il tuo account
            </p>

            <div className="bg-slate-100 p-4 rounded-lg mb-6 border-l-4 border-purple-500">
              <p className="text-xs text-slate-900 mb-2">
                <strong>Email:</strong> {inviteData.email}
              </p>
              <p className="text-xs text-slate-900">
                <strong>Role:</strong> {inviteData.role.charAt(0).toUpperCase() + inviteData.role.slice(1)}
              </p>
            </div>

            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              {/* Password Field */}
              <div>
                <label className="block font-medium mb-2 text-slate-900 text-sm">
                  Password <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-3 text-slate-400" />
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="At least 8 characters"
                    className={`w-full py-3 pl-10 pr-3 rounded-lg text-sm outline-none transition-colors ${
                      errors.password
                        ? 'border border-red-600 focus:ring-1 focus:ring-red-600'
                        : 'border border-slate-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                    }`}
                  />
                </div>
                {errors.password && (
                  <p className="text-red-600 text-xs mt-1">{errors.password}</p>
                )}
              </div>

              {/* Confirm Password Field */}
              <div>
                <label className="block font-medium mb-2 text-slate-900 text-sm">
                  Confirm Password <span className="text-red-600">*</span>
                </label>
                <div className="relative">
                  <Lock size={18} className="absolute left-3 top-3 text-slate-400" />
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="Re-enter your password"
                    className={`w-full py-3 pl-10 pr-3 rounded-lg text-sm outline-none transition-colors ${
                      errors.confirmPassword
                        ? 'border border-red-600 focus:ring-1 focus:ring-red-600'
                        : 'border border-slate-300 focus:border-purple-500 focus:ring-1 focus:ring-purple-500'
                    }`}
                  />
                </div>
                {errors.confirmPassword && (
                  <p className="text-red-600 text-xs mt-1">{errors.confirmPassword}</p>
                )}
              </div>

              {/* Password Requirements */}
              <div className="bg-slate-50 p-3 rounded-lg text-xs text-gray-600">
                <p className="mb-1.5 font-medium">Password must contain:</p>
                <ul className="ml-4 space-y-0.5">
                  <li>At least 8 characters</li>
                  <li>One uppercase letter</li>
                  <li>One number</li>
                  <li>One special character (!@#$%^&*)</li>
                </ul>
              </div>

              <button
                type="submit"
                disabled={loading}
                className={`w-full py-3 rounded-lg font-semibold text-sm cursor-pointer transition-colors mt-2 ${
                  loading
                    ? 'bg-slate-300 text-white cursor-not-allowed'
                    : 'bg-purple-500 text-white hover:bg-purple-600'
                }`}
              >
                {loading ? 'Activating...' : 'Activate Account'}
              </button>
            </form>
          </>
        )}

        {/* Success State */}
        {step === 'success' && (
          <div className="text-center">
            <CheckCircle size={48} className="text-green-600 mx-auto mb-4" />
            <h2 className="text-2xl text-slate-900 mb-2">
              Account Activated!
            </h2>
            <p className="text-gray-600 mb-6">
              Your account has been successfully activated. Redirecting to dashboard...
            </p>
          </div>
        )}

        {/* Error State */}
        {step === 'error' && (
          <div className="text-center">
            <AlertCircle size={48} className="text-red-600 mx-auto mb-4" />
            <h2 className="text-2xl text-slate-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              {errorMessage}
            </p>
            <button
              onClick={() => navigate('/')}
              className="px-5 py-2.5 bg-purple-500 text-white rounded-lg font-semibold cursor-pointer hover:bg-purple-600 transition-colors"
            >
              Return to Home
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
